import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AddOnsSelector from "../components/AddOns";
import "./Pricing.css";
import "../styles/theme.css";
import Footer from "../components/Footer";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
const Pricing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [includeHosting, setIncludeHosting] = useState(false);
  const [hostingDuration, setHostingDuration] = useState("");

  useEffect(() => {
    let base = getBasePrice();
    let addOnTotal = selectedAddOns.reduce(
      (sum, addon) => sum + addon.price,
      0
    );
    let hostingCost = 0;

    if (selectedPlan === "one-time" && includeHosting) {
      if (hostingDuration === "1") hostingCost = 100;
      if (hostingDuration === "2") hostingCost = 180;
      if (hostingDuration === "3") hostingCost = 250;
    }

    setTotalPrice(base + addOnTotal + hostingCost);
  }, [selectedPlan, selectedAddOns, includeHosting, hostingDuration]);

  const [
    userDetailsFromContextOrFirebase,
    setUserDetailsFromContextOrFirebase,
  ] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserDetailsFromContextOrFirebase({
          name: user.displayName || "User",
          email: user.email || "",
        });
      }
    });

    return () => unsubscribe();
  }, []);

  // ✅ Pricing Plans
  const plans = [
    {
      key: "startup",
      price: { monthly: 999, yearly: 9600 },
      name: t("pricing.plans.startup.name"),
      features: t("pricing.plans.startup.features", { returnObjects: true }),
    },
    {
      key: "advanced",
      price: { monthly: 1499, yearly: 14400 },
      name: t("pricing.plans.advanced.name"),
      features: t("pricing.plans.advanced.features", { returnObjects: true }),
    },
    {
      key: "premium",
      price: { monthly: 2499, yearly: 24000 },
      name: t("pricing.plans.premium.name"),
      features: t("pricing.plans.premium.features", { returnObjects: true }),
    },
  ];

  // ✅ One-Time Website Plans
  const oneTimePlans = [
   {
      key: "basic",
      price: 8999,
      name: t("pricing.one_time.basic.name"),
      features: t("pricing.one_time.basic.features", { returnObjects: true }),
    },
    {
      key: "standard",
      price: 12999,
      name: t("pricing.one_time.standard.name"),
      features: t("pricing.one_time.standard.features", {
        returnObjects: true,
      }),
    },
    {
      key: "premium",
      price: 18999,
      name: t("pricing.one_time.premium.name"),
      features: t("pricing.one_time.premium.features", { returnObjects: true }),
    },
  ];

  // ✅ Payment Methods (Dummy Data)
  const paymentMethods = useMemo(
    () => [
      { key: "stripe", name: t("pricing.methods.card") },
      { key: "paypal", name: t("pricing.methods.paypal") },
      { key: "bank", name: t("pricing.methods.bank") },
    ],
    [t] // re-compute when language changes
  );

  // ✅ Get Base Price for Selected Plan
  const getBasePrice = () => {
    if (!selectedPlan) return 0;
    return billingCycle === "monthly"
      ? plans.find((p) => p.key === selectedPlan).price.monthly
      : plans.find((p) => p.key === selectedPlan).price.yearly;
  };

  // ✅ Handle Plan Selection
  const handlePlanSelection = (planKey) => {
    setSelectedPlan(planKey);
    setSelectedAddOns([]); // Reset add-ons when switching plans
    setTotalPrice(getBasePrice()); // Reset price when switching plans
  };

  // ✅ Handle Checkout Function
  const handleCheckout = () => {
    if (!selectedPlan || !selectedPayment) {
      alert(t("pricing.select_plan_and_payment"));
      return;
    }

    navigate("/checkout", {
      state: {
        selectedPlan,
        selectedAddOns,
        totalPrice,
        selectedPayment,
        billingCycle,
        includeHosting,
        hostingDuration,
        userDetails: userDetailsFromContextOrFirebase,
      },
    });
  };
  // ✅ Plan Comparison Table Data
  const featureList = [
    {
      label: t("pricing.features.mobile"),
      startup: true,
      growth: true,
      pro: true,
    },
    {
      label: t("pricing.features.hosting"),
      startup: true,
      growth: true,
      pro: true,
    },
    {
      label: t("pricing.features.contact"),
      startup: true,
      growth: true,
      pro: true,
    },
    {
      label: t("pricing.features.seo"),
      startup: true,
      growth: true,
      pro: true,
    },
    {
      label: t("pricing.features.maintenance"),
      startup: true,
      growth: true,
      pro: true,
    },
    {
      label: t("pricing.features.blog"),
      startup: false,
      growth: true,
      pro: true,
    },
    {
      label: t("pricing.features.analytics"),
      startup: false,
      growth: true,
      pro: true,
    },
    {
      label: t("pricing.features.ecommerce"),
      startup: false,
      growth: false,
      pro: true,
    },
    {
      label: t("pricing.features.booking"),
      startup: false,
      growth: false,
      pro: true,
    },
    {
      label: t("pricing.features.payment"),
      startup: false,
      growth: false,
      pro: true,
    },
  ];

  const handleOneTimeClick = (plan) => {
    navigate("/checkout", {
      state: {
        selectedPlan: plan.key,
        selectedAddOns: [],
        totalPrice: plan.price,
        paymentMethod: "Stripe (Test)", // or whatever default you use
        billingCycle: null, // one-time doesn't have billing cycle
        isOneTime: true,
        userDetails: userDetailsFromContextOrFirebase,
      },
    });
  };

  return (
    <div className="pricing-container">
      <div className="main-content">
        <h1>{t("pricing.title")}</h1>

        {/* ✅ Billing Cycle Toggle */}
        <div className="billing-toggle">
          <button
            className={billingCycle === "monthly" ? "active" : ""}
            onClick={() => setBillingCycle("monthly")}
          >
            {t("pricing.monthly")}
          </button>
          <button
            className={billingCycle === "yearly" ? "active" : ""}
            onClick={() => setBillingCycle("yearly")}
          >
            {t("pricing.yearly")}
          </button>
        </div>

        {/* ✅ Pricing Plans */}
        <div className="plans-container">
          {plans.map((plan) => {
            const planFeatures = featureList.map((feature) => {
              const isIncluded =
                plan.key === "startup"
                  ? feature.startup
                  : plan.key === "advanced"
                  ? feature.growth
                  : feature.pro;

              return (
                <li
                  key={feature.label}
                  className={isIncluded ? "included" : "not-included"}
                >
                  <span
                    className={isIncluded ? "tick" : "cross"}
                    aria-hidden="true"
                  >
                    {isIncluded ? "✓" : "✕"}
                  </span>
                  {feature.label}
                </li>
              );
            });

            return (
              <div
                key={plan.key}
                className={`plan-box ${
                  selectedPlan === plan.key ? "selected" : ""
                } ${plan.key === "advanced" ? "popular-plan" : ""}`}
                onClick={() => handlePlanSelection(plan.key)}
              >
                {plan.key === "advanced" && (
                  <div className="popular-badge">
                    {t("pricing.most_popular")}
                  </div>
                )}

                <h2>{plan.name}</h2>
                <p className="plan-price">
                  {billingCycle === "monthly"
                    ? `${plan.price.monthly} kr`
                    : `${plan.price.yearly} kr`}
                  {billingCycle === "yearly" && (
                    <span className="discount"> ({t("pricing.save_20")})</span>
                  )}
                </p>

                <ul className="plan-features">{planFeatures}</ul>
              </div>
            );
          })}
        </div>
        {/* ✅ One-Time Website Options */}
        <div className="one-time-section">
          <h2>{t("pricing.one_time_website")}</h2>
          <div className="plans-container">
            {oneTimePlans.map((plan) => (
              <div
                key={plan.key}
                className="plan-box"
                onClick={() => handleOneTimeClick(plan)}
              >
                <h3>{plan.name}</h3>
                <p className="plan-price">{plan.price} kr</p>
                <ul className="plan-features">
                  {plan.features.map((feat, index) => (
                    <li key={index} className="included">
                      <span className="tick" aria-hidden="true">
                        ✓
                      </span>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ✅ Add-ons Section */}
        {selectedPlan && (
          <AddOnsSelector
            basePrice={getBasePrice()}
            selectedAddOns={selectedAddOns}
            setSelectedAddOns={setSelectedAddOns}
            onUpdateTotal={setTotalPrice}
          />
        )}

        {selectedPlan === "one-time" && (
          <div className="hosting-addon">
            <label>
              <input
                type="checkbox"
                checked={includeHosting}
                onChange={() => setIncludeHosting(!includeHosting)}
              />
              {t("pricing.include_hosting")}
            </label>

            {includeHosting && (
              <select
                value={hostingDuration}
                onChange={(e) => setHostingDuration(e.target.value)}
              >
                <option value="">{t("pricing.select_duration")}</option>
                <option value="1">1 Year - $100</option>
                <option value="2">2 Years - $180 (Save 10%)</option>
                <option value="3">3 Years - $250 (Save 17%)</option>
              </select>
            )}
          </div>
        )}

        {/* ✅ Total Price Summary */}
        {selectedPlan && (
          <div className="summary-container">
            <h2>{t("pricing.summary")}</h2>
            <p>
              {t("pricing.selected_plan")}:{" "}
              {plans.find((p) => p.key === selectedPlan).name}
            </p>
            {selectedAddOns.length > 0 && (
              <ul>
                {selectedAddOns.map((addon, index) => (
                  <li key={index}>
                    {addon.name}: ${addon.price}
                  </li>
                ))}
              </ul>
            )}
            <h3>
              {t("pricing.total")}: ${totalPrice}
            </h3>
          </div>
        )}

        {/* ✅ Payment Method Selection */}
        {selectedPlan && (
          <div className="payment-container">
            <h2>{t("pricing.payment_method")}</h2>
            <div className="payment-options">
              {paymentMethods.map((method) => (
                <div
                  key={method.key}
                  className={`payment-option ${
                    selectedPayment === method.key ? "selected" : ""
                  }`}
                  onClick={() =>
                    setSelectedPayment((prev) =>
                      prev === method.key ? null : method.key
                    )
                  }
                >
                  {method.name}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ✅ Checkout Button */}
        {selectedPlan && selectedPayment && (
          <button className="checkout-btn" onClick={handleCheckout}>
            {t("pricing.checkout")}
          </button>
        )}
      </div>

      {/* ✅ Footer */}
      <Footer />
    </div>
  );
};

export default Pricing;
